// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT: PROPOSITION DE PARTICIPANTS À UN COMITÉ (mai 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Factorisation des helpers de rendu et de l'appel API qui étaient dupliqués
// entre `page-invite-committee.ts` (page autonome) et `page-meeting.ts`
// (CTA "Proposer une liste de participants" dans le formulaire de réunion).
//
// API :
//   - `fetchInvitationProposal(scope)` : POST `/invitation-points/compute`.
//   - `renderProposalPanel(host, result, options?)` : injecte le HTML dans
//     `host` et câble les `bulkActions` (boutons qui agissent sur les users
//     cochés via des checkboxes en début de ligne).
//
// Mode lecture seule (sans `bulkActions`) : pas de checkboxes ni de toolbar
// — utilisé par #invite-committee.
// Mode multi-select (avec `bulkActions`) : chaque section affiche une
// checkbox par utilisateur + une toolbar avec "Cocher tous", "Décocher tous",
// et les boutons custom passés en `bulkActions` qui reçoivent la liste des
// users cochés de la section. Utilisé par #meeting/:id pour pousser une
// sélection dans Animateurs ou Participants en un clic.

import { invitationPointsApi } from '../api/invitation-points.js';
import { relativeScoreColor } from '../domain/invitee-highlight.js';

export interface ProposalScope {
  programId: number | null;
  projectId: number | null;
  structureId?: number | null;
  committeeId: number;
  withRisks: boolean;
}

export interface PilotUser {
  user_id?: number;
  trigram?: string | null;
  name: string;
  pilot_of?: string[];
}

export interface OwnershipReason {
  role: 'R' | 'RO' | 'A' | 'C' | string;
  project_code?: string | null;
  risk_code?: string | null;
  risk_id?: number | null;
  action_code?: string | null;
  action_id?: number | null;
}

export interface ScoredUser extends PilotUser {
  score?: number;
  reasons?: OwnershipReason[];
}

export interface InvitationResult {
  committee_pilots: PilotUser[];
  project_program_pilots: PilotUser[];
  structure_responsibles?: PilotUser[];
  scored: ScoredUser[];
  scope: {
    project_ids: number[];
    with_risks: boolean;
    committee_invite_pilot?: boolean;
  };
  counts?: {
    actions_from_committee?: number;
    actions_from_risks?: number;
  };
}

export interface ProposalBulkAction {
  /** Libellé affiché sur le bouton (ex. "A / R +"). */
  label: string;
  /** Tooltip (title HTML) — facultatif. */
  title?: string;
  /** Appelée au clic, avec la liste des users cochés de la section. */
  onClick: (users: PilotUser[]) => void;
}

export interface ProposalRenderOptions {
  /**
   * Boutons custom dans la toolbar de chaque section, agissant sur les
   * utilisateurs cochés de cette section. Active aussi le mode multi-select
   * (checkboxes en début de ligne + CTAs "Cocher tous" / "Décocher tous").
   * Si omis ou vide, la liste est en lecture seule.
   */
  bulkActions?: ProposalBulkAction[];
}

// ─── Appel API ──────────────────────────────────────────────────────────────

export async function fetchInvitationProposal(scope: ProposalScope): Promise<InvitationResult> {
  const payload = {
    program_id: scope.programId,
    project_id: scope.projectId,
    structure_id: scope.structureId ?? null,
    committee_id: scope.committeeId,
    with_risks: scope.withRisks,
  };
  return (await invitationPointsApi.computeInvitationPoints(payload)) as InvitationResult;
}

// ─── Helpers de rendu (purs) ────────────────────────────────────────────────

// Échelle de couleurs : domain/invitee-highlight.ts (source unique, testée
// hors DOM — la fiche réunion souligne ses participants avec la même).

function _ownershipReasons(reasons: ReadonlyArray<OwnershipReason> | undefined): OwnershipReason[] {
  const filtered = (reasons || []).filter((r) => r.role === 'R' || r.role === 'RO');
  return filtered.sort((a, b) => {
    if (a.role !== b.role) return a.role === 'RO' ? -1 : 1;
    const pa = a.project_code || '';
    const pb = b.project_code || '';
    // `numeric: true` : trie correctement les codes avec suffixe numérique
    // (PROJ-2 avant PROJ-10) — sinon le lexical met PROJ-10 avant PROJ-2.
    if (pa !== pb) return pa.localeCompare(pb, undefined, { numeric: true });
    const sa =
      a.role === 'RO'
        ? a.risk_code || `R-${a.risk_id || ''}`
        : a.action_code || `A-${a.action_id || ''}`;
    const sb =
      b.role === 'RO'
        ? b.risk_code || `R-${b.risk_id || ''}`
        : b.action_code || `A-${b.action_id || ''}`;
    // Fix bug latent 24/05/2026 : A-10 / R-10 venaient avant A-2 / R-2 en
    // tri lexical pur. `numeric: true` aligne sur l'ordre attendu utilisateur.
    return sa.localeCompare(sb, undefined, { numeric: true });
  });
}

function _renderOwnershipExplanation(reasons: ReadonlyArray<OwnershipReason> | undefined): string {
  const filtered = _ownershipReasons(reasons);
  if (!filtered.length) return '<span style="color:var(--text3);font-style:italic">—</span>';
  const ro = filtered
    .filter((r) => r.role === 'RO')
    .map((r) => {
      const proj = r.project_code || '?';
      const code = r.risk_code || (r.risk_id ? `R-${r.risk_id}` : '?');
      return `${proj}-${code}`;
    });
  const ra = filtered
    .filter((r) => r.role === 'R')
    .map((r) => {
      const proj = r.project_code || '?';
      const code = r.action_code || (r.action_id ? `A-${r.action_id}` : '?');
      return `${proj}-${code}`;
    });
  const lines: string[] = [];
  if (ro.length) lines.push(`<div>${esc(`${t('vocabulary.risk')} ${ro.join(', ')}`)}</div>`);
  if (ra.length) lines.push(`<div>${esc(`${t('vocabulary.action')} ${ra.join(', ')}`)}</div>`);
  return lines.join('');
}

// Toolbar affichée au-dessus de chaque tableau quand `bulkActions` est fourni
// et que la section n'est pas vide. `Cocher tous` / `Décocher tous` sont
// built-in, les autres boutons viennent de bulkActions.
function _renderSectionToolbar(
  section: string,
  bulkActions: ReadonlyArray<ProposalBulkAction>,
): string {
  if (!bulkActions.length) return '';
  const customs = bulkActions
    .map(
      (a, i) =>
        `<button type="button" class="btn btn-sm btn-secondary" data-ip-bulk-action="${i}" data-ip-section="${section}"${
          a.title ? ` title="${esc(a.title)}"` : ''
        } style="padding:2px 10px;font-size:11px">${esc(a.label)}</button>`,
    )
    .join('');
  return `<div data-ip-section-toolbar="${section}" style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px;padding:6px 0;border-bottom:1px solid var(--border)">
    <button type="button" class="btn btn-sm btn-secondary" data-ip-check-all data-ip-section="${section}" style="padding:2px 10px;font-size:11px">${esc(t('pageInviteCommittee.btnCheckAll'))}</button>
    <button type="button" class="btn btn-sm btn-secondary" data-ip-uncheck-all data-ip-section="${section}" style="padding:2px 10px;font-size:11px">${esc(t('pageInviteCommittee.btnUncheckAll'))}</button>
    <span style="flex:1"></span>
    ${customs}
  </div>`;
}

function _checkboxCell(section: string, userId: number | undefined, checked = false): string {
  if (!userId) return `<td style="padding:4px 6px;width:1px"></td>`;
  return `<td style="padding:4px 6px;width:1px"><input type="checkbox" data-ip-checkbox data-ip-section="${section}" data-ip-user-id="${userId}"${checked ? ' checked' : ''} style="cursor:pointer;accent-color:var(--accent)"></td>`;
}

function _renderPilotsList(
  list: ReadonlyArray<PilotUser>,
  opts: {
    section: string;
    showPilotOf?: boolean;
    bulkActions: ReadonlyArray<ProposalBulkAction>;
    /** Si true et que les checkboxes sont actives, pré-coche chaque ligne. */
    defaultChecked?: boolean;
  },
): string {
  if (!list.length)
    return `<div style="color:var(--text3);font-size:12px;font-style:italic;padding:4px 2px">${esc(t('pageInviteCommittee.noneSimple'))}</div>`;
  const hasCheckbox = opts.bulkActions.length > 0;
  const preCheck = hasCheckbox && !!opts.defaultChecked;
  return `${_renderSectionToolbar(opts.section, opts.bulkActions)}
  <table style="width:100%;font-size:13px;border-collapse:collapse" data-ip-section-table="${opts.section}">
    <thead><tr>
      ${hasCheckbox ? `<th style="padding:4px 6px;width:1px"></th>` : ''}
      <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px;width:90px">${esc(t('pageInviteCommittee.colTrigram'))}</th>
      <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px">${esc(t('pageInviteCommittee.colName'))}</th>
      ${opts.showPilotOf ? `<th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px">${esc(t('pageInviteCommittee.colPilotOf'))}</th>` : ''}
    </tr></thead>
    <tbody>
      ${list
        .map(
          (u) => `<tr style="vertical-align:top">
        ${hasCheckbox ? _checkboxCell(opts.section, u.user_id, preCheck) : ''}
        <td style="padding:4px 6px;color:var(--text2);font-weight:600" title="${esc(u.name)}">${esc(u.trigram || '')}</td>
        <td style="padding:4px 6px">${esc(u.name)}</td>
        ${opts.showPilotOf ? `<td style="padding:4px 6px;color:var(--text2);font-size:12px">${u.pilot_of && u.pilot_of.length ? u.pilot_of.map((s) => `<div>${esc(s)}</div>`).join('') : '<span style="color:var(--text3);font-style:italic">—</span>'}</td>` : ''}
      </tr>`,
        )
        .join('')}
    </tbody>
  </table>`;
}

function _renderScoredCards(
  list: ReadonlyArray<ScoredUser>,
  section: string,
  bulkActions: ReadonlyArray<ProposalBulkAction>,
): string {
  if (!list.length)
    return `<div style="color:var(--text3);font-size:12px;font-style:italic;padding:4px 2px">${esc(t('pageInviteCommittee.noScored'))}</div>`;
  const maxScore = list.reduce((m, u) => Math.max(m, u.score || 0), 0);
  const target = maxScore * 0.6 || 1;
  const hasCheckbox = bulkActions.length > 0;
  return `${_renderSectionToolbar(section, bulkActions)}
  <table style="width:100%;font-size:13px;border-collapse:collapse;table-layout:fixed" data-ip-section-table="${section}">
    <thead><tr>
      ${hasCheckbox ? `<th style="padding:4px 6px;width:28px"></th>` : ''}
      <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px;width:80px">${esc(t('pageInviteCommittee.colTrigram'))}</th>
      <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px;width:160px">${esc(t('pageInviteCommittee.colName'))}</th>
      <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px;width:30%">${esc(t('pageInviteCommittee.colRelativeScore'))}</th>
      <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px">${esc(t('pageInviteCommittee.colResponsibleFor'))}</th>
    </tr></thead>
    <tbody>
      ${list
        .map((u) => {
          const normalized = ((u.score || 0) / target) * 100;
          const width = Math.min(normalized, 100);
          const color = relativeScoreColor(u.score || 0, maxScore);
          // `data-ip-*` : repères pour la mise en évidence des invités manquants
          // (cf. page-meeting-invitees.ts). `data-ip-color` porte la couleur de
          // LA barre plutôt que le score : chaque section a son propre
          // `maxScore`, donc recalculer la couleur ailleurs donnerait un
          // soulignement qui contredit la barre d'à côté.
          return `<tr style="vertical-align:top;border-top:1px solid var(--border)">
          ${hasCheckbox ? _checkboxCell(section, u.user_id) : ''}
          <td data-ip-trigram data-ip-user-id="${u.user_id || 0}" data-ip-color="${color}" data-ip-name="${esc(u.name)}" style="padding:6px;color:var(--text2);font-weight:600;overflow:hidden;text-overflow:ellipsis" title="${esc(u.name)}">${esc(u.trigram || '')}</td>
          <td style="padding:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(u.name)}">${esc(u.name)}</td>
          <td style="padding:6px">
            <div style="display:flex;align-items:center;gap:8px">
              <div style="flex:1;background:var(--bg2,#eef0f3);height:10px;border-radius:5px;overflow:hidden;position:relative">
                <div style="width:${width.toFixed(1)}%;height:100%;background:${color};transition:width .2s ease"></div>
              </div>
              <span style="min-width:54px;text-align:right;font-size:11px;color:var(--text3)">${u.score || 0} ${esc(t('pageInviteCommittee.pointSuffix'))}</span>
            </div>
          </td>
          <td style="padding:6px;color:var(--text2);font-size:11px;line-height:1.4">${_renderOwnershipExplanation(u.reasons)}</td>
        </tr>`;
        })
        .join('')}
    </tbody>
  </table>`;
}

// ─── Rendu + câblage ────────────────────────────────────────────────────────

const SECTION_COMMITTEE = 'committee';
const SECTION_PROJ_PROG = 'project_program';
const SECTION_STRUCTURE = 'structure_responsibles';
const SECTION_SCORED = 'scored';

export function renderProposalPanel(
  host: HTMLElement,
  result: InvitationResult,
  options: ProposalRenderOptions = {},
): void {
  const bulkActions: ReadonlyArray<ProposalBulkAction> = options.bulkActions ?? [];

  // Map user_id → PilotUser, par section, pour résoudre les sélections au
  // moment du clic d'un bulkAction.
  const usersBySection = new Map<string, Map<number, PilotUser>>();
  function indexSection(section: string, list: ReadonlyArray<PilotUser>): void {
    const map = new Map<number, PilotUser>();
    for (const u of list) {
      if (u.user_id) map.set(u.user_id, u);
    }
    usersBySection.set(section, map);
  }
  const structureResp = result.structure_responsibles || [];
  indexSection(SECTION_COMMITTEE, result.committee_pilots);
  if (result.scope.committee_invite_pilot) {
    indexSection(SECTION_PROJ_PROG, result.project_program_pilots);
  }
  indexSection(SECTION_STRUCTURE, structureResp);
  indexSection(SECTION_SCORED, result.scored);

  const totalCount =
    result.committee_pilots.length +
    (result.scope.committee_invite_pilot ? result.project_program_pilots.length : 0) +
    structureResp.length +
    result.scored.length;

  // Les sections "Pilotes" sont pré-cochées par défaut en mode multi-select :
  // ils sont les invités évidents d'un comité, l'utilisateur ne fera donc
  // qu'éventuellement décocher au lieu de devoir tout cocher.
  const sectionCommittee = `
    <fieldset style="border:1px solid var(--border);border-radius:var(--radius);padding:8px 12px;margin-bottom:10px">
      <legend style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--text3);padding:0 4px">${esc(t('pageInviteCommittee.legendCommitteePilots'))}</legend>
      ${_renderPilotsList(result.committee_pilots, { section: SECTION_COMMITTEE, bulkActions, defaultChecked: true })}
    </fieldset>`;

  let sectionProjProg = '';
  if (result.scope.committee_invite_pilot) {
    sectionProjProg = `
      <fieldset style="border:1px solid var(--border);border-radius:var(--radius);padding:8px 12px;margin-bottom:10px">
        <legend style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--text3);padding:0 4px">${esc(t('pageInviteCommittee.legendProjectProgramPilots'))}</legend>
        ${_renderPilotsList(result.project_program_pilots, { section: SECTION_PROJ_PROG, showPilotOf: true, bulkActions, defaultChecked: true })}
      </fieldset>`;
  }

  let sectionStructure = '';
  if (structureResp.length) {
    sectionStructure = `
      <fieldset style="border:1px solid var(--border);border-radius:var(--radius);padding:8px 12px;margin-bottom:10px">
        <legend style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--text3);padding:0 4px">${esc(t('pageInviteCommittee.legendStructureResponsibles'))}</legend>
        ${_renderPilotsList(structureResp, { section: SECTION_STRUCTURE, bulkActions, defaultChecked: true })}
      </fieldset>`;
  }

  const sectionScored = `
    <fieldset style="border:1px solid var(--border);border-radius:var(--radius);padding:8px 12px">
      <legend style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--text3);padding:0 4px">${esc(t('pageInviteCommittee.legendOtherParticipants'))}</legend>
      <div style="display:flex;gap:18px;font-size:12px;color:var(--text2);margin:4px 2px 10px;flex-wrap:wrap">
        <span><strong>${(result.counts && result.counts.actions_from_committee) || 0}</strong> ${esc(t('pageInviteCommittee.actionsFromCommittee'))}</span>
        <span><strong>${(result.counts && result.counts.actions_from_risks) || 0}</strong> ${esc(t('pageInviteCommittee.actionsFromRisks'))}</span>
      </div>
      ${_renderScoredCards(result.scored, SECTION_SCORED, bulkActions)}
    </fieldset>`;

  const html = `
    <div class="card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
        <h3 style="font-size:15px;margin:0">${esc(t('pageInviteCommittee.resultTitle').replace('{n}', String(totalCount)))}</h3>
        <span style="font-size:11px;color:var(--text3)">${esc(
          t('pageInviteCommittee.scopeSummary')
            .replace('{n}', String(result.scope.project_ids.length))
            .replace(
              '{risks}',
              result.scope.with_risks
                ? t('common.yes').toLowerCase()
                : t('common.no').toLowerCase(),
            ),
        )}</span>
      </div>
      ${sectionCommittee}
      ${sectionProjProg}
      ${sectionStructure}
      ${sectionScored}
    </div>`;

  // HTML construit avec esc() sur chaque valeur utilisateur, pas de XSS.
  // eslint-disable-next-line no-restricted-syntax
  host.innerHTML = html;

  if (bulkActions.length === 0) return;

  // Câblage : cocher / décocher tous → toutes les checkboxes de la section.
  host
    .querySelectorAll<HTMLButtonElement>('[data-ip-check-all], [data-ip-uncheck-all]')
    .forEach((btn) => {
      btn.addEventListener('click', () => {
        const section = btn.dataset['ipSection'] || '';
        const check = btn.hasAttribute('data-ip-check-all');
        host
          .querySelectorAll<HTMLInputElement>(`[data-ip-checkbox][data-ip-section="${section}"]`)
          .forEach((cb) => {
            cb.checked = check;
          });
      });
    });

  // Câblage : bulkAction → récupère les checkboxes cochés de la section et
  // résout chaque user_id via la map indexée plus haut.
  host.querySelectorAll<HTMLButtonElement>('[data-ip-bulk-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const actionIdx = parseInt(btn.dataset['ipBulkAction'] || '0');
      const section = btn.dataset['ipSection'] || '';
      const action = bulkActions[actionIdx];
      if (!action) return;
      const checked = host.querySelectorAll<HTMLInputElement>(
        `[data-ip-checkbox][data-ip-section="${section}"]:checked`,
      );
      const map = usersBySection.get(section) ?? new Map<number, PilotUser>();
      const users: PilotUser[] = [];
      checked.forEach((cb) => {
        const uid = parseInt(cb.dataset['ipUserId'] || '0');
        const u = map.get(uid);
        if (u) users.push(u);
      });
      action.onClick(users);
    });
  });
}
